import CloseIcon from "@mui/icons-material/Close";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    IconButton,
    Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as constants from "../../constants";
import CustomDatePicker from "../../shared/CustomDate";
import { postBlackBaudTransaction } from "../fiscalUpgrade/services/BlackBaud/BlackBaudServices";
import { notifyUser } from "../fiscalUpgrade/Utils/notifyUser";

type Props = {
    open: boolean;
    handleClose: () => void;
    saveChanges: (date?: string) => void;
};

export default function PostDailyTransactionsPopUp({ open, handleClose, saveChanges }: Props) {
    const { t } = useTranslation();
    const translate = t as (key: string) => string;

    const [selectedDateState, setSelectedDateState] = useState<Dayjs | null>(null);
    const [error, setError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    // maxDate = yesterday (disable today and future dates)
    const maxDate: Dayjs = useMemo(() => dayjs().subtract(1, "day"), []);

    // Clear state when dialog closes
    useEffect(() => {
        if (!open) {
            setSelectedDateState(null);
            setError("");
        }
    }, [open]);

    const handlePost = async () => {
        if (!selectedDateState) {
            setError(translate("pleaseSelectADate"));
            return;
        }

        setError("");
        try {
            setLoading(true);
            const transactionData = selectedDateState.format("YYYY-MM-DD");
            const response = await postBlackBaudTransaction(transactionData);
            if (response && response.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(response.data.message, "success", translate("postDailyTransactions"));
            } else {
                console.error("Error in posting daily transactions:", response);
                notifyUser(response.data.message, "error", translate("postDailyTransactions"));
            }

        } catch (err: any) {
            notifyUser(err?.response?.data?.message || "Unexpected error occurred", 'error', translate("postDailyTransactions"));
            setError(translate("errorOccurred"));
        } finally {
            setLoading(false);
            await Promise.resolve(saveChanges(selectedDateState.format("YYYY-MM-DD")));
            setSelectedDateState(null);
            handleClose();
        }
    };

    const handleCancel = () => {
        setSelectedDateState(null);
        setError("");
        handleClose();
    };

    return (
        <Dialog open={open} fullWidth maxWidth="xs"  >
            <DialogTitle
                sx={{
                    backgroundColor: "#059ca9",
                    color: "white",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                {translate("postDailyTransactions")}
                <IconButton onClick={handleCancel} sx={{ color: "white", padding: 0 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ py: 1 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                        Select the date for which daily transactions should be posted to Blackbaud
                    </Typography>

                    <CustomDatePicker
                        labelText={translate("date")}
                        required
                        value={selectedDateState}
                        onChange={(newValue) => {
                            setSelectedDateState(newValue);
                            if (error) setError("");
                        }}
                        error={Boolean(error)}
                        maxDate={maxDate}
                        sx={{ width: "100%" }}
                    />

                    {error && (
                        <FormHelperText error sx={{ mt: 1 }}>
                            {error}
                        </FormHelperText>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={handleCancel}>
                    {translate("cancel")}
                </Button>
                <Button variant="contained" color="primary" onClick={handlePost}> {translate("postTransaction")}
                </Button>
            </DialogActions>

            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: "flex",
                    flexDirection: "column",
                }}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Dialog>
    );
}
